/**
 * Single frontend interpretation contract for /ai-coach/generate-training-plan
 * responses. TrainingPlanSetup and TrainingPlanDraftPanel must both consult
 * this helper so they cannot disagree about what a backend status means.
 *
 * Backend statuses are emitted by ai_coach_plan_generation_service.py:
 *   VALIDATED_DRAFT | NEEDS_INFORMATION | VALIDATION_FAILED |
 *   MODEL_UNAVAILABLE | IDEMPOTENCY_CONFLICT | SAFETY_HANDOFF |
 *   SECURITY_REJECTED | OUT_OF_COACHING_SCOPE
 *
 * Anything else must fail closed (keep the wizard open) — never silently
 * treat it as success.
 */

export const PLAN_GENERATION_RESULT_KIND = Object.freeze({
  DRAFT_READY: "DRAFT_READY",
  NEEDS_INFORMATION: "NEEDS_INFORMATION",
  VALIDATION_FAILED: "VALIDATION_FAILED",
  MODEL_UNAVAILABLE: "MODEL_UNAVAILABLE",
  IDEMPOTENCY_CONFLICT: "IDEMPOTENCY_CONFLICT",
  SAFETY_HANDOFF: "SAFETY_HANDOFF",
  SECURITY_REJECTED: "SECURITY_REJECTED",
  OUT_OF_COACHING_SCOPE: "OUT_OF_COACHING_SCOPE",
  UNKNOWN: "UNKNOWN",
});

const DEFAULT_MESSAGES = {
  NEEDS_INFORMATION:
    "More information is required before your training plan can be built. Please review your setup.",
  VALIDATION_FAILED:
    "Your training plan could not be validated. Please review your setup and try again.",
  MODEL_UNAVAILABLE:
    "Plan generation is temporarily unavailable. Please try again shortly.",
  IDEMPOTENCY_CONFLICT:
    "A plan-generation request is already in progress. Please refresh and try again.",
  SAFETY_HANDOFF:
    "Plan generation was paused for a safety review of your inputs.",
  SECURITY_REJECTED:
    "Plan generation could not proceed with the information provided.",
  OUT_OF_COACHING_SCOPE:
    "Plan generation could not proceed with the information provided.",
  UNKNOWN:
    "We could not complete plan generation. Please try again.",
};

/**
 * Interpret a plan-generation response.
 *
 * @param {any} response
 * @returns {{
 *   kind: string,
 *   ok: boolean,
 *   closeDialog: boolean,
 *   retryable: boolean,
 *   response: any,
 *   message: string,
 * }}
 */
export function interpretPlanGenerationResult(response) {
  const status = response && typeof response === "object" ? response.status : undefined;
  const reply = response && typeof response.reply === "string" ? response.reply : null;

  switch (status) {
    case "VALIDATED_DRAFT":
      return {
        kind: PLAN_GENERATION_RESULT_KIND.DRAFT_READY,
        ok: true,
        closeDialog: true,
        retryable: false,
        response,
        message: reply || "",
      };
    case "NEEDS_INFORMATION":
      return {
        kind: PLAN_GENERATION_RESULT_KIND.NEEDS_INFORMATION,
        ok: false,
        closeDialog: false,
        retryable: true,
        response,
        message: reply || DEFAULT_MESSAGES.NEEDS_INFORMATION,
      };
    case "VALIDATION_FAILED":
      return {
        kind: PLAN_GENERATION_RESULT_KIND.VALIDATION_FAILED,
        ok: false,
        closeDialog: false,
        retryable: true,
        response,
        message: reply || DEFAULT_MESSAGES.VALIDATION_FAILED,
      };
    case "MODEL_UNAVAILABLE":
      return {
        kind: PLAN_GENERATION_RESULT_KIND.MODEL_UNAVAILABLE,
        ok: false,
        closeDialog: false,
        retryable: true,
        response,
        message: reply || DEFAULT_MESSAGES.MODEL_UNAVAILABLE,
      };
    case "IDEMPOTENCY_CONFLICT":
      return {
        kind: PLAN_GENERATION_RESULT_KIND.IDEMPOTENCY_CONFLICT,
        ok: false,
        closeDialog: false,
        retryable: false,
        response,
        message: reply || DEFAULT_MESSAGES.IDEMPOTENCY_CONFLICT,
      };
    case "SAFETY_HANDOFF":
    case "SECURITY_REJECTED":
    case "OUT_OF_COACHING_SCOPE":
      return {
        kind: PLAN_GENERATION_RESULT_KIND[status],
        ok: false,
        closeDialog: false,
        retryable: false,
        response,
        message: reply || DEFAULT_MESSAGES[status],
      };
    default:
      return {
        kind: PLAN_GENERATION_RESULT_KIND.UNKNOWN,
        ok: false,
        closeDialog: false,
        retryable: false,
        response,
        message: reply || DEFAULT_MESSAGES.UNKNOWN,
      };
  }
}
